import {
	createDefaultValue,
	deriveTypes,
	resolveSchema
} from "@jsonforms/core";
import type Ajv from "ajv";
import type { ErrorObject } from "ajv";
import isEqual from "lodash/isEqual";

import type { JsonSchemaDefinition } from "./types";
import { cloneValue } from "./values";

export type AlternativeKind = "oneOf" | "anyOf" | "type";

export interface SchemaAlternative {
	label: string;
	schema: JsonSchemaDefinition;
}

export interface SchemaAlternatives {
	kind: AlternativeKind;
	options: SchemaAlternative[];
}

export interface AlternativeChange {
	value: unknown;
	replace: boolean;
	confirm: boolean;
}

interface AlternativeSource {
	schema: JsonSchemaDefinition;
	pointer?: string;
}

const alternatives = new WeakMap<JsonSchemaDefinition, WeakMap<JsonSchemaDefinition, SchemaAlternatives>>();

const sources = new WeakMap<SchemaAlternatives, AlternativeSource[]>();

const structuralKeywords = new Set(["required", "additionalProperties", "type", "enum", "const"]);

const jsonTypes = ["string", "number", "boolean", "object", "array", "null"];

const typeLabels: Record<string, string> = {
	string: "Text",
	number: "Number",
	integer: "Whole number",
	boolean: "Yes / No",
	object: "Object",
	array: "List",
	null: "No value"
};

function resolvedSchema(schema: JsonSchemaDefinition, root: JsonSchemaDefinition): JsonSchemaDefinition {
	return resolveSchema(schema, "#", root) as JsonSchemaDefinition ?? schema;
}

function schemaPointer(root: JsonSchemaDefinition, target: JsonSchemaDefinition): string | undefined {
	const visited = new WeakSet<object>();

	function find(value: unknown, pointer: string): string | undefined {
		if(value === target) {
			return pointer;
		}

		if(value === null || typeof value !== "object" || visited.has(value)) {
			return undefined;
		}

		visited.add(value);

		for(const [key, child] of Object.entries(value)) {
			const segment = key.replace(/~/g, "~0").replace(/\//g, "~1");
			const match = find(child, `${pointer}/${segment}`);

			if(match !== undefined) {
				return match;
			}
		}

		return undefined;
	}

	return find(root, "#");
}

function combinedAdditionalItems(
	base: JsonSchemaDefinition["additionalItems"],
	branch: JsonSchemaDefinition["additionalItems"]
): JsonSchemaDefinition["additionalItems"] {
	if(base === false || branch === false) {
		return false;
	}

	if(typeof base === "object" && typeof branch === "object") {
		return combinedSchema(base, branch);
	}

	return typeof branch === "object" ? branch : typeof base === "object" ? base : branch ?? base;
}

/** Expose both property sets to object renderers while retaining their conjunction. */
function combinedSchema(base: JsonSchemaDefinition, branch: JsonSchemaDefinition): JsonSchemaDefinition {
	const properties = { ...base.properties };

	for(const [key, property] of Object.entries(branch.properties ?? {})) {
		properties[key] = properties[key] ? combinedSchema(properties[key], property) : property;
	}

	const combined: JsonSchemaDefinition = {
		...base,
		...branch,
		allOf: [base, branch]
	};

	if(base.properties || branch.properties) {
		combined.properties = properties;
	}

	if(base.required || branch.required) {
		combined.required = [...new Set([...(base.required ?? []), ...(branch.required ?? [])])];
	}

	if(base.metadata || branch.metadata) {
		combined.metadata = { ...base.metadata,
			...branch.metadata };
	}

	if(base.items !== undefined && branch.items !== undefined) {
		const baseItems = base.items;
		const branchItems = branch.items;

		if(Array.isArray(baseItems) || Array.isArray(branchItems)) {
			const length = Math.max(Array.isArray(baseItems) ? baseItems.length : 0, Array.isArray(branchItems) ? branchItems.length : 0);

			combined.items = Array.from({ length }, (_, index) => {
				const common = Array.isArray(baseItems)
					? baseItems[index] ?? (typeof base.additionalItems === "object" ? base.additionalItems : undefined)
					: baseItems;
				const specific = Array.isArray(branchItems)
					? branchItems[index] ?? (typeof branch.additionalItems === "object" ? branch.additionalItems : undefined)
					: branchItems;

				return common && specific ? combinedSchema(common, specific) : common ?? specific;
			});
			combined.additionalItems = combinedAdditionalItems(
				Array.isArray(baseItems) ? base.additionalItems : baseItems,
				Array.isArray(branchItems) ? branch.additionalItems : branchItems
			);
		} else {
			combined.items = combinedSchema(baseItems, branchItems);
		}
	} else if(base.additionalItems !== undefined || branch.additionalItems !== undefined) {
		combined.additionalItems = combinedAdditionalItems(base.additionalItems, branch.additionalItems);
	}

	return combined;
}

/** Expose conjunctive control structure without changing its validation schemas. */
function renderingSchema(
	schema: JsonSchemaDefinition,
	root: JsonSchemaDefinition,
	visited = new Set<JsonSchemaDefinition>()
): JsonSchemaDefinition {
	const resolved = resolvedSchema(schema, root);

	if(!resolved || typeof resolved !== "object" || visited.has(resolved)) {
		return resolved;
	}

	const ancestors = new Set(visited).add(resolved);
	const presentation = { ...resolved };

	if(resolved.properties) {
		presentation.properties = Object.fromEntries(
			Object.entries(resolved.properties)
				.map(([key, property]) => [key, renderingSchema(property, root, ancestors)])
		);
	}

	if(resolved.items !== undefined) {
		presentation.items = Array.isArray(resolved.items)
			? resolved.items.map(item => renderingSchema(item, root, ancestors))
			: renderingSchema(resolved.items, root, ancestors);
	}

	if(typeof resolved.additionalItems === "object") {
		presentation.additionalItems = renderingSchema(resolved.additionalItems, root, ancestors);
	}

	for(const component of resolved.allOf ?? []) {
		if(!component || typeof component !== "object") {
			continue;
		}

		const constraint = renderingSchema(component, root, ancestors);
		const combined = combinedSchema(presentation, constraint);

		presentation.type = presentation.type ?? constraint.type;
		presentation.format = presentation.format ?? constraint.format;

		if(constraint.metadata) {
			presentation.metadata = { ...constraint.metadata,
				...presentation.metadata };
		}

		if(combined.items !== undefined) {
			presentation.items = combined.items;
		}

		if(combined.additionalItems !== undefined) {
			presentation.additionalItems = combined.additionalItems;
		}

		if(constraint.properties) {
			const properties = { ...presentation.properties };

			for(const [key, property] of Object.entries(constraint.properties)) {
				properties[key] = properties[key] ? combinedSchema(properties[key], property) : property;
			}

			presentation.properties = properties;
		}

		if(constraint.required) {
			presentation.required = [...new Set([...(presentation.required ?? []), ...constraint.required])];
		}
	}

	return presentation;
}

export function getSchemaAlternatives(
	schema: JsonSchemaDefinition,
	root: JsonSchemaDefinition
): SchemaAlternatives | undefined {
	let rootAlternatives = alternatives.get(root);

	if(!rootAlternatives) {
		rootAlternatives = new WeakMap();
		alternatives.set(root, rootAlternatives);
	}

	const cached = rootAlternatives.get(schema);

	if(cached) {
		return cached;
	}

	const resolved = resolvedSchema(schema, root);
	const oneOf = resolved.oneOf;
	const isChoice = oneOf?.length && oneOf.every(branch => Object.prototype.hasOwnProperty.call(branch, "const"));

	if(isChoice || resolved.enum || Object.prototype.hasOwnProperty.call(resolved, "const")) {
		return undefined;
	}

	const kind = oneOf?.length ? "oneOf" : resolved.anyOf?.length ? "anyOf" : "type";
	const types = Array.isArray(resolved.type) ? [...new Set(resolved.type)] : [];
	const branches: (JsonSchemaDefinition | boolean)[] = kind === "type" ? types.map(type => ({ type })) : resolved[kind] ?? [];

	if(kind === "type" && types.filter(type => type !== "null").length < 2) {
		return undefined;
	}

	const base = { ...resolved };

	if(kind !== "type") {
		delete base[kind];
	}

	const renderingBase = renderingSchema(base, root);
	const parentPointer = kind === "type" ? undefined : schemaPointer(root, resolved);
	const branchSources: AlternativeSource[] = [];
	const definition: SchemaAlternatives = {
		kind,
		options: branches.flatMap((branch, index) => {
			const branchSchema: JsonSchemaDefinition | boolean = typeof branch === "boolean" ? branch : resolvedSchema(branch, root);

			if(branchSchema === false) {
				return [];
			}

			const resolvedBranch = branchSchema === true ? { type: base.type ?? jsonTypes } : branchSchema;
			const renderingBranch = renderingSchema(resolvedBranch, root);
			const optionSchema = kind === "type"
				? { ...renderingBase,
					type: resolvedBranch.type }
				: combinedSchema(renderingBase, renderingBranch);
			const pointer = parentPointer !== undefined
				? `${parentPointer}/${kind}/${index}`
				: typeof branch === "object" ? schemaPointer(root, branch) : undefined;

			branchSources.push({
				schema: branchSchema === true ? optionSchema : resolvedBranch,
				pointer
			});

			return [{
				label: kind === "type"
					? typeLabels[String(resolvedBranch.type)] ?? String(resolvedBranch.type)
					: (typeof branch === "object" ? branch.title : undefined) ?? resolvedBranch.title ?? `Option ${index + 1}`,
				schema: optionSchema
			}];
		})
	};

	sources.set(definition, branchSources);
	rootAlternatives.set(schema, definition);

	return definition;
}

function matchesType(schema: JsonSchemaDefinition, value: unknown): boolean {
	const types = deriveTypes(schema);

	return types.length === 0 || types.some(type => {
		switch(type) {
			case "null": return value === null;
			case "array": return Array.isArray(value);
			case "object": return value !== null && typeof value === "object" && !Array.isArray(value);
			case "integer": return typeof value === "number" && Number.isInteger(value);
			case "number": return typeof value === "number";
			default: return typeof value === type;
		}
	});
}

function hasStructuralError(error: ErrorObject): boolean {
	if(structuralKeywords.has(error.keyword)) {
		return true;
	}

	const nested: unknown = error.params.errors;

	return Array.isArray(nested) && nested.some((child: unknown) => {
		if(child === null || typeof child !== "object" || !("keyword" in child)) {
			return false;
		}

		return hasStructuralError(child as ErrorObject);
	});
}

export function inferAlternativeIndex(
	definition: SchemaAlternatives,
	data: unknown,
	ajv: Ajv,
	root: JsonSchemaDefinition
): number {
	const branchSources = sources.get(definition);
	const rootId = root.$id?.replace(/#.*$/, "") ?? "";
	let typedFallback = -1;

	for(let index = 0; index < definition.options.length; index++) {
		const source = branchSources?.[index];
		const schema = source?.schema ?? definition.options[index].schema;

		if(!matchesType(schema, data)) {
			continue;
		}

		if(typedFallback < 0) {
			typedFallback = index;
		}

		if(definition.kind === "type") {
			return index;
		}

		try {
			let validate = source?.pointer
				? ajv.getSchema(`${rootId}${source.pointer}`)
				: ajv.compile(schema);

			if(!validate && source?.pointer) {
				ajv.compile(root);
				validate = ajv.getSchema(`${rootId}${source.pointer}`);
			}

			if(validate) {
				validate(data);

				if(!validate.errors?.some(hasStructuralError)) {
					return index;
				}
			}
		}catch{
			// Rendering can still use the value's type when an external reference is unavailable.
		}
	}

	return Math.max(typedFallback, 0);
}

export function getAlternativeChange(
	definition: SchemaAlternatives,
	index: number,
	data: unknown,
	root: JsonSchemaDefinition
): AlternativeChange {
	const option = definition.options[index];

	if(!option) {
		return {
			value: data,
			replace: false,
			confirm: false
		};
	}

	const defaultSchema = definition.kind === "type" ? option.schema : sources.get(definition)?.[index].schema ?? option.schema;
	const hasConstant = Object.prototype.hasOwnProperty.call(defaultSchema, "const");
	let value: unknown = hasConstant ? cloneValue(defaultSchema.const) : createDefaultValue(defaultSchema, root);

	if(!hasConstant && !matchesType(defaultSchema, value)) {
		value = createDefaultValue({ ...defaultSchema,
			default: undefined }, root);
	}

	const retainValue = hasConstant ? isEqual(data, value) : data === undefined || matchesType(option.schema, data);

	if(definition.kind === "anyOf" && retainValue) {
		return {
			value: data,
			replace: false,
			confirm: false
		};
	}

	return {
		value,
		replace: true,
		confirm: data !== undefined
	};
}
